import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-overflow-menu>` says about itself.
 *
 * The one fact that explains most of the rest: THE MENU IS NOT INSIDE THIS ELEMENT. It is built into
 * its own shadow host appended to the end of `<body>`, positioned `fixed` against the trigger's live
 * screen rectangle and repositioned on scroll and resize. That is why nothing clips it — not a
 * table's horizontal scroller, not an action bar, not a stage on a reference page — and it is why
 * `::part(menu)` and `::part(item)` no longer exist: `::part()` reaches into a component's OWN
 * shadow tree and no further, and the menu is not in it any more. `::part(trigger)` is the only
 * styling seam left. Custom properties DO inherit into the portaled tree, so the token table below
 * is now the whole of how the menu is restyled; that is deliberate, and the price of never clipping.
 *
 * Two consequences worth stating because a reader would otherwise be surprised:
 *  - The menu's accessible name is the trigger's label copied onto it, rather than a reference back
 *    to the trigger. An id in one shadow tree cannot be addressed from another, so there is no
 *    honest way to point at it.
 *  - The portal is built on connect, and on open. An element that never reaches the page never
 *    builds one, so a list surface that constructs rows off-DOM and throws some away does not leave
 *    orphaned nodes on `<body>`.
 *
 * WHAT A PAGE CAN READ BACK: `open` is a public READ-ONLY property — a page can ask whether the menu
 * is showing. It is not an input, because nothing can set it, and it is not a method, because it
 * takes no call. It is declared under `state`, and mentioned again under `placing`.
 */
const docs: ComponentDocs = {
  aliases: ['kebab menu', 'three-dot menu', 'more menu', 'context menu', 'ellipsis menu', 'row actions', 'dropdown menu'],

  answers: [
    'hide the actions people rarely use behind one control',
    'put Edit, Clone and Delete on a table row',
    'offer a destructive action without putting it in front of everybody',
    'fit six actions where there is room for two',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'recordAction'],

  inputs: [
    {
      name: 'items',
      // No attribute: a list of actions cannot be written in one. Declarative <button> children are
      // read as an alternative source on connect — see the slots note.
      type: 'json',
      description:
        'The actions, as { value, label, disabled?, danger?, separator? }. A separator entry draws a ' +
        'rule and is skipped by the keyboard; a danger entry is drawn in the danger ink; a disabled ' +
        'entry is drawn faded, refused, and stepped over by the arrow keys.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What this menu is called. Becomes the accessible name of both the trigger and the menu it ' +
        'opens — "Row actions", "More order actions". Read once, on connect.',
      default: 'More actions',
    },
    {
      name: 'align',
      attribute: 'align',
      type: 'text',
      options: ['start'],
      fallback: 'end',
      description:
        'Set to "start" to open the panel to the RIGHT of the trigger, for a trigger sitting at the ' +
        'left of its row. Anything else, including leaving it off, right-aligns the panel to the ' +
        'trigger — the correct behaviour for the row-end position this piece is normally in. Read live ' +
        'on every reposition, so it may be changed after placement.',
    },
  ],

  events: [
    {
      name: 'caos-menu-select',
      when:
        'An enabled item is activated, by pointer or by Enter or Space. The menu closes itself and ' +
        'returns focus to the trigger; DOING the thing is the host’s job.',
      carries: '{ value } — the value of the item that was chosen.',
    },
  ],

  // Genuinely none: this component renders no <slot> at all. Light-DOM <button> children ARE read
  // on connect as an alternative source for the items — and are then hidden, because the portaled
  // menu is authoritative. They are data, not content, and nothing put inside them is projected.
  state: [
    {
      name: 'open',
      type: 'boolean',
      description: 'Whether the menu panel is showing.',
    },
  ],

  slots: [],

  tokens: [
    {
      token: '--caos-layer-dropdown',
      controls:
        'Which layer the floating menu asks for. A NAME rather than a stacking number, so the platform ' +
        'can resolve it against where the menu actually sits — the same menu belongs below a dialog on ' +
        'a page and above it inside one. Falls back to the value the sibling panels carry until the ' +
        'token ships.',
    },
    { token: '--caos-shadow-float', controls: 'The elevation of the floating menu — it is the one floating surface.' },
    { token: '--caos-color-surface', controls: 'The fill of the menu panel.' },
    { token: '--caos-color-border', controls: 'The outline of the panel, and the rule a separator draws.' },
    { token: '--caos-radius', controls: 'The corner radius of the panel and of the trigger.' },
    { token: '--caos-color-surface-2', controls: 'The highlight behind a hovered or focused item, and the open trigger.' },
    { token: '--caos-color-danger', controls: 'The ink of an item marked danger.' },
    { token: '--caos-color-danger-bg', controls: 'The highlight behind a hovered danger item.' },
    { token: '--caos-color-danger-text', controls: 'The ink of a danger item while it is hovered.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on the keyboard-focused trigger.' },
  ],

  // One seam, and only one: the menu is portaled out of the shadow tree, and `::part()` reaches only
  // into a shadow root, so nothing inside the panel is addressable from outside.
  parts: [
    { part: 'trigger', is: 'the control that opens the menu' },
  ],

  methods: [],

  placing:
    'At the RIGHT-HAND END of the row it belongs to — the last cell of a table row, the trailing edge ' +
    'of an ActionBar’s overflow slot, the end of a card’s header. The panel right-aligns to the trigger ' +
    'and opens DOWNWARD, flipping only far enough to stay inside the viewport, so a trigger placed at ' +
    'the left of a wide row opens its panel back across the row rather than under itself; that is what ' +
    '`align="start"` is for. The trigger itself is a 32px inline-block, so it sits on a control line ' +
    'without disturbing it. Nothing around it needs to make room: the panel is portaled to the end of ' +
    'the document, so no ancestor’s overflow, transform or stacking context can clip it. A page that ' +
    'needs to know whether the panel is showing can read the `open` property.',

  whenToUse:
    'A set of actions that are all secondary, or the tail of a set whose first one or two earned a ' +
    'place in the open. Reach for it wherever repeating three buttons on every row of a table would ' +
    'drown the rows.',

  insteadUse: [
    {
      name: 'Button',
      when:
        'The action is the one people came to do. Anything primary put behind this trigger is an action ' +
        'nobody finds. Note that this piece never replaces Button — it holds actions Button would draw.',
    },
    {
      name: 'ActionBar',
      when:
        'The actions deserve to be visible and laid out in the open. That one arranges a row; this one ' +
        'collapses a set behind one control. They pair — this goes in that bar’s overflow slot — so the ' +
        'question is which actions go where, not which component to use.',
    },
    {
      name: 'PicklistField',
      when:
        'The list is a VALUE being chosen rather than an action being taken. Choosing from this menu does ' +
        'something; it does not set anything.',
    },
    {
      name: 'ObjectCaret',
      when:
        'The dropdown belongs to a tab in the workspace frame and lists what can be done with an object. ' +
        'That is a frame part and is not placed on a page.',
    },
  ],

  accessibility:
    'The trigger is a real button carrying `aria-haspopup="menu"` and a live `aria-expanded`, and the ' +
    'panel is a `role="menu"` of `role="menuitem"`s, so the whole pattern is announced correctly without ' +
    'the author doing anything. The full keyboard model is handled: Enter, Space and Arrow Down open on ' +
    'the first item, Arrow Up opens on the last, Arrow Up/Down and Home/End roam within the menu, Enter ' +
    'and Space activate, Escape closes and returns focus to the trigger, Tab and Shift+Tab stay inside ' +
    'the open menu and cycle its items, and a pointer press outside closes without moving focus. Disabled items carry ' +
    '`aria-disabled` and are skipped rather than focused and refused. Because the panel lives in its own ' +
    'shadow tree at the end of the document, it cannot be pointed at from the trigger, so it carries the ' +
    'trigger’s own name instead. Left to the author: give the menu a `label`. Every unlabelled one on a ' +
    'page is announced as "More actions", which on a table of forty rows names nothing.',

  examples: [
    {
      key: 'row-actions',
      label: 'Actions on a row',
      summary:
        'The ordinary case: three actions behind one trigger at the end of a row, the last of them ' +
        'destructive and drawn in the danger ink.',
      // measured: 109px menu + 4px gap. The stage puts the trigger at its right-hand end, because the
      // panel right-aligns to the trigger and a trigger at the left would open its panel off the side.
      stage: { opensBelow: 120, alignEnd: true },
      placement: {
        component: 'caos-overflow-menu',
        inputs: {
          label: 'Row actions',
          items: [
            { value: 'edit', label: 'Edit' },
            { value: 'clone', label: 'Clone' },
            { value: 'delete', label: 'Delete', danger: true },
          ],
        },
      },
    },
    {
      key: 'grouped',
      label: 'Grouped, with one refused',
      summary:
        'A longer set divided by a separator, with an action that is unavailable — faded, refused to the ' +
        'pointer, and stepped over by the arrow keys rather than focused and then rejected.',
      // 109px was measured for the three-item menu above; this one carries five items and a separator,
      // so the reservation is scaled from that rather than measured. Under-reserving is cosmetic only —
      // the panel floats free of the stage, so it overlaps what is below it rather than being clipped.
      stage: { opensBelow: 200, alignEnd: true },
      placement: {
        component: 'caos-overflow-menu',
        inputs: {
          label: 'Order actions',
          items: [
            { value: 'export', label: 'Export as PDF' },
            { value: 'share', label: 'Share with the account' },
            { value: 'revise', label: 'Start a revision' },
            { value: 'approve', label: 'Approve', disabled: true },
            { value: 'sep', label: '', separator: true },
            { value: 'delete', label: 'Delete order', danger: true },
          ],
        },
      },
    },
    {
      key: 'align-start',
      label: 'Opening the other way',
      summary:
        'The same menu on a trigger that is NOT at the end of its row — the panel is told to open to the ' +
        'right of the trigger instead of back across it.',
      stage: { opensBelow: 120 },
      placement: {
        component: 'caos-overflow-menu',
        inputs: {
          label: 'Section actions',
          align: 'start',
          items: [
            { value: 'rename', label: 'Rename section' },
            { value: 'move', label: 'Move up' },
            { value: 'remove', label: 'Remove section', danger: true },
          ],
        },
      },
    },
  ],
};

export default docs;
